import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BoardColumn, BoardField } from '../../board-v3.types';
import { BoardService } from '../../../../services/board.service';
import { ModalService } from '../../../../services/modal.service';
import { NotificationService } from '../../../../services/notifications.service';
import { FileUploadNewComponent } from '../../../../modals/file-upload-new/file-upload-new.component';
import { environment } from '../../../../../environments/environment';
import { formatFullName } from '../../../../helpers';

type ImportRowMode = 'none' | 'csv' | 'manual';

interface ImportCsvColumnOption {
  value: string;
  label: string;
}

interface ImportManagerOption {
  id: string;
  name: string;
  avatarUrl?: string;
}

interface ImportFieldRow {
  field: BoardField;
  key: string;
  mode: ImportRowMode;
  csvColumn: string | null;
  control: FormControl<unknown>;
}

const MANAGER_FIELD_KEYS = ['assigned_id', 'assigned', 'manager_id'];

@Component({
  selector: 'app-board-v3-import-mapping',
  templateUrl: './import-mapping.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ImportMappingComponent implements OnChanges {
  @Input() funnelId: string | null = null;
  @Input() fields: BoardField[] = [];
  @Input() columns: BoardColumn[] = [];

  @Output() close = new EventEmitter<{ imported: boolean } | void>();

  private readonly boardService = inject(BoardService);
  private readonly modalService = inject(ModalService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  currentCsvFile: any = null;
  csvColumns: { name: string; sample: string[] }[] = [];
  csvData: Record<string, string>[] = [];
  csvColumnOptions: ImportCsvColumnOption[] = [];
  availableFields: BoardField[] = [];
  rows: ImportFieldRow[] = [];
  columnOptions: { id: string; name: string }[] = [];
  managerOptions: ImportManagerOption[] = [];
  selectedColumnId: string | null = null;
  isLoadingFields = false;
  isImporting = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['funnelId'] && this.funnelId) {
      this.loadFields(this.funnelId);
      this.loadManagers();
    }
    if (changes['fields'] && !this.availableFields.length) {
      this.availableFields = this.fields ?? [];
      this.buildRows();
    }
    if (changes['columns']) {
      this.columnOptions = (this.columns ?? []).map((column) => ({ id: column.id, name: column.name }));
      if (!this.selectedColumnId) this.selectedColumnId = this.columnOptions[0]?.id ?? null;
    }
  }

  get hasFile(): boolean {
    return !!this.currentCsvFile;
  }

  get fileName(): string {
    return this.currentCsvFile?.name || this.currentCsvFile?.original_name || '';
  }

  get previewRows(): Record<string, string>[] {
    return this.csvData.slice(0, 5);
  }

  get csvMappedCount(): number {
    return this.rows.filter((row) => row.mode === 'csv' && !!row.csvColumn).length;
  }

  get manualCount(): number {
    return this.rows.filter((row) => row.mode === 'manual' && this.hasManualValue(row)).length;
  }

  get canImport(): boolean {
    return !!this.selectedColumnId && !!this.csvMappedCount && !this.isImporting;
  }

  isManagerField(field: BoardField): boolean {
    return MANAGER_FIELD_KEYS.includes(String(field.identifier ?? '').toLowerCase());
  }

  isCsvColumnUsed(row: ImportFieldRow): boolean {
    if (row.mode !== 'csv' || !row.csvColumn) return false;

    return this.rows.some((other) => other !== row && other.mode === 'csv' && other.csvColumn === row.csvColumn);
  }

  getCsvSample(row: ImportFieldRow): string {
    if (!row.csvColumn) return '';

    const column = this.csvColumns.find((item) => item.name === row.csvColumn);
    return (column?.sample ?? []).filter(Boolean).slice(0, 3).join(', ');
  }

  onModeChange(row: ImportFieldRow, mode: ImportRowMode): void {
    if (this.isImporting || row.mode === mode) return;

    row.mode = mode;
    if (mode !== 'csv') row.csvColumn = null;
    if (mode !== 'manual') row.control.reset(null);
  }

  onCancel(): void {
    if (this.isImporting) return;
    this.close.emit();
  }

  onUploadClick(): void {
    this.modalService
      .openModal({
        title: 'upload_csv',
        translate: true,
        component: FileUploadNewComponent,
        height: 'auto',
        data: {
          max_upload_files: 1,
          allowed_media_types: ['text/csv', 'application/vnd.ms-excel', 'text/plain'],
          max_upload_size: 10,
          is_tmp: true,
          channel_type: null,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((uploadedFiles: any[]) => {
        if (uploadedFiles?.length) this.loadCsvFile(uploadedFiles[0]);
      });
  }

  onImport(): void {
    if (!this.funnelId || !this.selectedColumnId || !this.currentCsvFile || this.isImporting) return;

    const fieldMappings = this.rows
      .filter((row) => row.mode === 'csv' && !!row.csvColumn)
      .map((row) => ({ csv_column: row.csvColumn as string, target_field: row.key }));

    const fieldDefaults = this.rows
      .filter((row) => row.mode === 'manual' && this.hasManualValue(row))
      .map((row) => ({ target_field: row.key, value: row.control.value }));

    this.isImporting = true;
    this.boardService
      .importCsv(this.funnelId, {
        file_url: this.currentCsvFile.link || this.currentCsvFile.url,
        import_type: 'CSV',
        column_id: this.selectedColumnId,
        field_mappings: fieldMappings,
        field_defaults: fieldDefaults,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationService.showInfo('', this.translateService.instant('board_v3_import_started'));
          this.close.emit({ imported: true });
        },
        error: (error: any) => {
          this.isImporting = false;
          this.notificationService.showError(
            '',
            error?.error?.message || this.translateService.instant('board_v3_import_failed'),
          );
        },
      });
  }

  private loadFields(funnelId: string): void {
    this.isLoadingFields = true;
    this.boardService
      .getBoardFields(funnelId, 'FUNNEL')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fields) => {
          this.availableFields = fields;
          this.isLoadingFields = false;
          this.buildRows();
        },
        error: () => {
          this.availableFields = this.fields ?? [];
          this.isLoadingFields = false;
          this.buildRows();
        },
      });
  }

  private loadManagers(): void {
    this.boardService
      .getManagers({ page: 1, take: 500 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (managers) => {
          this.managerOptions = (managers ?? []).map((manager) => ({
            id: manager.id,
            name: formatFullName(manager.first_name, manager.last_name),
            avatarUrl: manager.avatar_url || undefined,
          }));
        },
        error: () => {
          this.managerOptions = [];
        },
      });
  }

  private loadCsvFile(csvFile: any): void {
    const fileUrl = csvFile?.link || csvFile?.url;
    if (!fileUrl) {
      this.notificationService.showError('', this.translateService.instant('board_v3_import_failed'));
      return;
    }

    fetch(fileUrl)
      .then((response) => response.text())
      .then((csvText) => {
        this.parseCsv(csvText);
        if (!this.csvColumns.length) {
          this.notificationService.showError('', this.translateService.instant('board_v3_import_empty_file'));
          return;
        }
        this.currentCsvFile = csvFile;
      })
      .catch(() => {
        this.notificationService.showError('', this.translateService.instant('board_v3_import_failed'));
      });
  }

  private parseCsv(csvText: string): void {
    const lines = (csvText ?? '').split(/\r?\n/).filter((line) => line.trim());
    if (!lines.length) {
      this.csvColumns = [];
      this.csvData = [];
      this.csvColumnOptions = [];
      return;
    }

    const delimiter = this.detectDelimiter(lines[0]);
    const headers = this.parseCsvLine(lines[0], delimiter);

    const data: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCsvLine(lines[i], delimiter);
      if (values.length > 0) {
        const row: Record<string, string> = {};
        headers.forEach((header, index) => (row[header] = values[index] || ''));
        data.push(row);
      }
    }

    this.csvData = data;
    this.csvColumns = headers.map((header) => ({
      name: header,
      sample: data.slice(0, 3).map((row) => row[header] || ''),
    }));
    this.csvColumnOptions = this.csvColumns.map((column) => ({ value: column.name, label: column.name }));
    this.autoSuggestCsvColumns();
  }

  private detectDelimiter(line: string): string {
    const delimiters = [',', ';', '\t'];
    let maxCount = 0;
    let detected = ',';
    for (const delimiter of delimiters) {
      const count = line.split(delimiter).length - 1;
      if (count > maxCount) {
        maxCount = count;
        detected = delimiter;
      }
    }
    return detected;
  }

  private parseCsvLine(line: string, delimiter: string = ','): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }

  private buildRows(): void {
    const previousRows = new Map(this.rows.map((row) => [row.key, row]));
    this.rows = this.availableFields.filter(v => !v.is_readonly).map((field) => {
      const key = field.identifier;
      const existing = previousRows.get(key);
      if (existing) return { ...existing, field };

      return {
        field,
        key,
        mode: 'none' as ImportRowMode,
        csvColumn: null,
        control: new FormControl<unknown>(null),
      };
    });

    this.autoSuggestCsvColumns();
  }

  private autoSuggestCsvColumns(): void {
    if (!this.csvColumns.length) return;

    const takenColumns = new Set(
      this.rows.filter((row) => row.mode === 'csv' && row.csvColumn).map((row) => row.csvColumn as string),
    );

    for (const row of this.rows) {
      if (row.mode !== 'none') continue;

      const match = this.suggestCsvColumn(row.field, takenColumns);
      if (!match) continue;

      row.mode = 'csv';
      row.csvColumn = match;
      takenColumns.add(match);
    }
  }

  private suggestCsvColumn(field: BoardField, taken: Set<string>): string | null {
    const label = (field.name ?? '').toLowerCase().trim();
    const identifier = String(field.identifier ?? '').toLowerCase().trim();

    const match = this.csvColumns.find((column) => {
      if (taken.has(column.name)) return false;

      const name = column.name.toLowerCase().trim();
      if (!name) return false;

      return name === label || name === identifier || (!!label && (name.includes(label) || label.includes(name)));
    });

    return match?.name ?? null;
  }

  private hasManualValue(row: ImportFieldRow): boolean {
    const value = row.control.value;
    return value !== null && value !== undefined && value !== '';
  }
}
